import { DragEvent, FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";
import { intakeVisual, type IntakeResponse } from "@/lib/api";
import type { Empresa } from "@/types";
import { extractApiError, formatearFechaCorta } from "@/lib/utils";

interface MensajeChatLocal {
  id: string;
  rol: "usuario" | "gema" | "sistema";
  contenido: string;
  archivos?: string[];
  timestamp: string;
  paso?: string;
}

const TIPOS_ACEPTADOS = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
];

export function ChatIntakePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search] = useSearchParams();
  const { sesion } = useAuth();
  const inicialesUsuario = (sesion?.sub ?? "TÚ")
    .split(/[.@_-]/)
    .map((p) => p.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2) || "TÚ";

  // Pre-relleno cuando llegamos desde un expediente con texto pobre
  // (banner "📎 Subir PDF" del ExpedienteDetail).
  const empresaInicial = (search.get("empresa") as Empresa) || "AFINIA";
  const [empresa, setEmpresa] = useState<Empresa>(empresaInicial);
  const [nic, setNic] = useState(search.get("nic") || "");
  const [numeroRadicado, setNumeroRadicado] = useState(
    search.get("numero_radicado") || ""
  );
  const [mensaje, setMensaje] = useState("");
  const [archivos, setArchivos] = useState<File[]>([]);
  const [arrastrando, setArrastrando] = useState(false);
  const [pendientes, setPendientes] = useState<string[]>([]);
  const [historial, setHistorial] = useState<MensajeChatLocal[]>([
    {
      id: crypto.randomUUID(),
      rol: "gema",
      contenido:
        "Hola, sube el PDF del radicado, la factura y/o la Orden de Servicio (puedes arrastrarlos aquí o usar el botón 📎). Procesaré el caso con visión y te muestro el resultado. Si encuentro el NIC y el número de radicado en el documento, no te los pediré.",
      timestamp: new Date().toISOString(),
    },
  ]);
  const refScroll = useRef<HTMLDivElement | null>(null);
  const refInputArchivo = useRef<HTMLInputElement | null>(null);
  const refNic = useRef<HTMLInputElement | null>(null);
  const refRadicado = useRef<HTMLInputElement | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      intakeVisual({
        empresa,
        nic: nic.trim(),
        numero_radicado: numeroRadicado.trim(),
        mensaje,
        archivos,
      }),
    onSuccess: async (data: IntakeResponse) => {
      // Mostrar los pasos uno por uno con un retraso pequeño para simular
      // progreso, aunque la API ya respondió completo.
      for (const paso of data.pasos) {
        await delay(350);
        agregarMensaje({
          rol: "gema",
          contenido: paso.mensaje,
          paso: paso.nombre,
        });
      }

      if (data.estado === "REQUIERE_INFO" && data.campos_pendientes.length > 0) {
        setPendientes(data.campos_pendientes);
        await delay(200);
        if (data.campos_pendientes.includes("nic")) {
          refNic.current?.focus();
        } else if (data.campos_pendientes.includes("numero_radicado")) {
          refRadicado.current?.focus();
        }
        return;
      }

      qc.invalidateQueries({ queryKey: ["expedientes"] });
      await delay(400);
      if (data.expediente_id) {
        navigate(`/expedientes/${data.expediente_id}`);
      }
    },
    onError: (err) => {
      agregarMensaje({
        rol: "sistema",
        contenido: `⚠ ${extractApiError(err, "Error procesando el documento.")}`,
      });
    },
  });

  useEffect(() => {
    if (refScroll.current) {
      refScroll.current.scrollTop = refScroll.current.scrollHeight;
    }
  }, [historial.length]);

  function agregarMensaje(m: Omit<MensajeChatLocal, "id" | "timestamp">) {
    setHistorial((prev) => [
      ...prev,
      {
        ...m,
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
      },
    ]);
  }

  function manejarArchivos(lista: FileList | File[]) {
    const nuevos = Array.from(lista).filter((f) =>
      TIPOS_ACEPTADOS.includes(f.type)
    );
    if (nuevos.length !== Array.from(lista).length) {
      agregarMensaje({
        rol: "sistema",
        contenido:
          "⚠ Sólo se aceptan PDF, PNG, JPEG o WEBP. Los demás archivos se ignoraron.",
      });
    }
    setArchivos((prev) => [...prev, ...nuevos]);
  }

  function manejarDrop(e: DragEvent<HTMLFormElement>) {
    e.preventDefault();
    setArrastrando(false);
    if (e.dataTransfer.files.length) {
      manejarArchivos(e.dataTransfer.files);
    }
  }

  function quitarArchivo(idx: number) {
    setArchivos((prev) => prev.filter((_, i) => i !== idx));
  }

  function manejarEnvio(e: FormEvent) {
    e.preventDefault();

    // Si hay pendientes, validamos que el operador los completó.
    if (pendientes.includes("nic") && !nic.trim()) {
      agregarMensaje({
        rol: "sistema",
        contenido: "⚠ Falta el NIC para continuar.",
      });
      refNic.current?.focus();
      return;
    }
    if (pendientes.includes("numero_radicado") && !numeroRadicado.trim()) {
      agregarMensaje({
        rol: "sistema",
        contenido: "⚠ Falta el número de radicado para continuar.",
      });
      refRadicado.current?.focus();
      return;
    }

    if (archivos.length === 0 && !mensaje.trim() && pendientes.length === 0) {
      agregarMensaje({
        rol: "sistema",
        contenido:
          "⚠ Adjunta al menos un documento o describe el reclamo en texto.",
      });
      return;
    }

    if (pendientes.length > 0) {
      const aportes: string[] = [];
      if (pendientes.includes("nic")) aportes.push(`NIC ${nic.trim()}`);
      if (pendientes.includes("numero_radicado"))
        aportes.push(`radicado ${numeroRadicado.trim()}`);
      agregarMensaje({
        rol: "usuario",
        contenido: `Datos suministrados: ${aportes.join(", ")}.`,
      });
      setPendientes([]);
    } else {
      agregarMensaje({
        rol: "usuario",
        contenido: mensaje || "(sin texto adicional)",
        archivos: archivos.map((f) => f.name),
      });
    }

    agregarMensaje({
      rol: "gema",
      contenido: "Procesando con visión y pipeline jurídico…",
      paso: "procesando",
    });

    mut.mutate();
  }

  const enviando = mut.isPending;
  const cerrado = mut.isSuccess && pendientes.length === 0;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />
      <main
        className="page page--wide"
        style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px", width: "100%" }}
      >
        {/* Breadcrumbs + page head — UI-4 alineado al diseño. */}
        <div
          className="crumbs"
          style={{
            display: "flex",
            gap: 6,
            fontSize: 12,
            color: "var(--ink-3)",
            marginBottom: 8,
          }}
        >
          <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>
            Bandeja
          </Link>
          <span className="sep">/</span>
          <span style={{ color: "var(--ink)" }}>Nuevo expediente</span>
        </div>
        <div
          className="page-head"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div className="eyebrow" style={{ marginBottom: 4 }}>
              Ingreso manual
            </div>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-serif, 'IBM Plex Serif', serif)",
                fontWeight: 500,
                fontSize: 28,
                color: "var(--ink)",
                lineHeight: 1.2,
              }}
            >
              Nuevo expediente
            </h1>
            <p
              className="sub muted"
              style={{
                margin: "6px 0 0",
                fontSize: 14,
                maxWidth: 640,
                lineHeight: 1.5,
              }}
            >
              Sube el radicado en PDF/imagen. GEMA leerá el documento con
              visión, clasificará la pretensión y proyectará la carta
              automáticamente.
            </p>
          </div>
          <Link to="/" className="btn btn--ghost">
            ← Volver
          </Link>
        </div>

        <div
          className="intake"
          style={{
            display: "grid",
            gridTemplateColumns: "300px 1fr",
            gap: 16,
          }}
        >
        {/* Panel izquierdo: identificación opcional */}
        <aside
          className="card ident-card"
          style={{ padding: 16, alignSelf: "start", position: "sticky", top: 16 }}
        >
          <h2
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink-2)",
              margin: "0 0 12px",
            }}
          >
            Identificación
          </h2>
          <div className="field" style={{ marginBottom: 12 }}>
            <label className="label" htmlFor="empresa">
              Empresa{" "}
              <span style={{ color: "var(--red, oklch(0.55 0.18 25))" }}>*</span>
            </label>
            <select
              id="empresa"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value as Empresa)}
              className="select"
              disabled={enviando || cerrado}
            >
              <option value="AFINIA">AFINIA</option>
              <option value="AIR_E">AIR-E</option>
            </select>
            <div className="hint">Siempre obligatoria.</div>
          </div>
          <div className="field" style={{ marginBottom: 12 }}>
            <label className="label" htmlFor="nic">
              NIC{" "}
              {pendientes.includes("nic") && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--amber-deep, oklch(0.45 0.16 75))",
                  }}
                >
                  · requerido
                </span>
              )}
            </label>
            <input
              ref={refNic}
              id="nic"
              type="text"
              value={nic}
              onChange={(e) => setNic(e.target.value)}
              className={
                "input mono" +
                (pendientes.includes("nic") ? " attention" : "")
              }
              placeholder="ej. 6600057"
              disabled={enviando || cerrado}
              style={
                pendientes.includes("nic")
                  ? {
                      outline:
                        "2px solid var(--amber-deep, oklch(0.55 0.16 75))",
                      outlineOffset: 1,
                    }
                  : undefined
              }
            />
            <div className="hint">Lo intentaré extraer del documento.</div>
          </div>
          <div className="field" style={{ marginBottom: 12 }}>
            <label className="label" htmlFor="radicado">
              Número de radicado{" "}
              {pendientes.includes("numero_radicado") && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--amber-deep, oklch(0.45 0.16 75))",
                  }}
                >
                  · requerido
                </span>
              )}
            </label>
            <input
              ref={refRadicado}
              id="radicado"
              type="text"
              value={numeroRadicado}
              onChange={(e) => setNumeroRadicado(e.target.value)}
              className="input mono"
              placeholder="ej. RE9421202608954"
              disabled={enviando || cerrado}
              style={
                pendientes.includes("numero_radicado")
                  ? {
                      outline:
                        "2px solid var(--amber-deep, oklch(0.55 0.16 75))",
                      outlineOffset: 1,
                    }
                  : undefined
              }
            />
            <div className="hint">GEMA lo busca en la página 1 del PDF.</div>
          </div>
          <hr
            style={{
              border: 0,
              borderTop: "1px solid var(--border)",
              margin: "14px 0",
            }}
          />
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            Cuando enviar
          </div>
          <p
            className="muted"
            style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}
          >
            La empresa siempre la define el analista (multi-tenant). NIC y
            radicado son opcionales: GEMA los lee del PDF y solo los solicita
            si no los encuentra.
          </p>
        </aside>

        {/* Panel derecho: chat (chat-shell del diseño) */}
        <section
          className="card chat-shell"
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 600,
          }}
        >
          <div className="chat-shell__head">
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div className="av av--bot">G</div>
              <div>
                <h3>Chat de captura · GEMA</h3>
                <div className="stamp">
                  claude-haiku-4.5 · contexto: Régimen 108 · sesión #
                  {numeroRadicado.slice(-4) || "nuevo"}
                </div>
              </div>
            </div>
            <span className="badge badge--green">
              <span className="dot" />
              Conectado
            </span>
          </div>

          <div
            ref={refScroll}
            style={{
              flex: 1,
              overflow: "auto",
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minHeight: 400,
            }}
          >
            {historial.map((m) => (
              <Mensaje key={m.id} m={m} inicialesUsuario={inicialesUsuario} />
            ))}
            {enviando && (
              <div
                className="muted"
                style={{ fontSize: 12, fontStyle: "italic" }}
              >
                GEMA está leyendo y razonando…
              </div>
            )}
          </div>

          {/* Progress stages — pipeline de la ingestación visual. */}
          <ProgressStages
            enviando={enviando}
            cerrado={cerrado}
            archivos={archivos.length}
          />

          <form
            onSubmit={manejarEnvio}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastrando(true);
            }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={manejarDrop}
            style={{
              borderTop: "1px solid var(--border)",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              background: arrastrando
                ? "var(--emerald-soft, oklch(0.95 0.04 162))"
                : "transparent",
              outline: arrastrando
                ? "2px solid var(--emerald, oklch(0.55 0.16 162))"
                : "none",
              outlineOffset: -2,
              transition: "background .15s, outline-color .15s",
            }}
          >
            <div className="composer__chips">
              {archivos.map((f, i) => (
                <span key={i} className="chip">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="2" y="1.5" width="7" height="8" rx="1" />
                  </svg>
                  {f.name}
                  <span className="muted">
                    · {(f.size / 1024).toFixed(0)} kB
                  </span>
                  <button
                    type="button"
                    onClick={() => quitarArchivo(i)}
                    aria-label={`Quitar ${f.name}`}
                    className="chip__remove"
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="chip chip--add"
                onClick={() => refInputArchivo.current?.click()}
                disabled={enviando || cerrado}
                title="Adjuntar PDF/imagen"
              >
                + Añadir archivo
              </button>
            </div>

            {pendientes.length > 0 && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--amber-deep, oklch(0.45 0.16 75))",
                  background: "var(--amber-soft, oklch(0.95 0.05 80))",
                  border: "1px solid oklch(0.85 0.10 80)",
                  borderRadius: 4,
                  padding: "8px 12px",
                  lineHeight: 1.5,
                }}
              >
                Completa los campos marcados en el panel izquierdo y haz click
                en <strong>Reintentar</strong> para continuar.
              </div>
            )}

            <input
              ref={refInputArchivo}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              style={{ display: "none" }}
              onChange={(e) =>
                e.target.files && manejarArchivos(e.target.files)
              }
            />
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    manejarEnvio(e as unknown as FormEvent);
                  }
                }}
                placeholder={
                  pendientes.length > 0
                    ? "Cuando completes los campos del panel, pulsa Reintentar."
                    : "Escribe una instrucción adicional al modelo… (ej: 'usar plantilla AVM si hay sospecha de medidor')"
                }
                rows={2}
                className="input"
                style={{ flex: 1, resize: "none" }}
                disabled={enviando || cerrado}
              />
              <button
                type="submit"
                className="btn btn--accent"
                disabled={enviando || cerrado}
              >
                {enviando
                  ? "Procesando…"
                  : pendientes.length > 0
                  ? "Reintentar"
                  : "Enviar"}
                {!enviando && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    style={{ marginLeft: 4 }}
                  >
                    <path
                      d="M3 8h10M8 3l5 5-5 5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>

            <div className="composer__footer">
              <span>
                Arrastra archivos aquí · PDF, JPG, PNG · máx 10 MB
              </span>
              <span>
                <span className="kbd">Enter</span> envía ·{" "}
                <span className="kbd">⇧ Enter</span> nueva línea
              </span>
            </div>
          </form>
        </section>
        </div>
      </main>
    </div>
  );
}

function Mensaje({ m, inicialesUsuario }: { m: MensajeChatLocal; inicialesUsuario: string }) {
  if (m.rol === "sistema") {
    return (
      <div
        style={{
          fontSize: 12,
          color: "var(--amber-deep, oklch(0.45 0.16 75))",
          background: "var(--amber-soft, oklch(0.95 0.05 80))",
          border: "1px solid oklch(0.85 0.10 80)",
          borderRadius: 4,
          padding: "8px 12px",
          lineHeight: 1.5,
        }}
      >
        {m.contenido}
      </div>
    );
  }
  const esGema = m.rol === "gema";
  return (
    <div className={`chat-row${esGema ? "" : " chat-row--user"}`}>
      {esGema && <div className="av av--bot">G</div>}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          maxWidth: "75%",
        }}
      >
        {m.paso && (
          <span
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 600,
              color: "var(--ink-3)",
            }}
          >
            {m.paso.replace(/_/g, " ")}
          </span>
        )}
        <div className={`bubble ${esGema ? "bubble--bot" : "bubble--user"}`}>
          {m.contenido}
          {m.archivos && m.archivos.length > 0 && (
            <ul
              style={{
                margin: "8px 0 0",
                padding: 0,
                listStyle: "none",
                fontSize: 12,
                opacity: 0.85,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {m.archivos.map((a, i) => (
                <li key={i}>📎 {a}</li>
              ))}
            </ul>
          )}
        </div>
        <span
          style={{
            fontSize: 10,
            color: esGema ? "var(--ink-3)" : "oklch(0.92 0.03 162)",
            alignSelf: esGema ? "flex-start" : "flex-end",
          }}
        >
          {formatearFechaCorta(m.timestamp)}
        </span>
      </div>
      {!esGema && (
        <div className="av av--user">{inicialesUsuario || "TÚ"}</div>
      )}
    </div>
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Pipeline visual de la ingestación (, design ref:
 * Desing/nuevo-expediente.html · progress-stages). Avanza el estado
 * según los flags del flujo:
 *   - 0: idle / esperando adjunto
 *   - 1: hay archivos → Adjuntar done
 *   - 2: enviando → Visión OCR current (las demás pending)
 *   - 7: cerrado → todo done
 */
const STAGES = [
  "Adjuntar",
  "Visión OCR",
  "Validar perfil",
  "Clasificar",
  "Proyectar",
  "Auditor",
  "Revisión",
] as const;

function ProgressStages({
  enviando,
  cerrado,
  archivos,
}: {
  enviando: boolean;
  cerrado: boolean;
  archivos: number;
}) {
  let avanzado = 0;
  if (cerrado) avanzado = STAGES.length;
  else if (enviando) avanzado = 2; // Adjuntar done + Visión OCR current
  else if (archivos > 0) avanzado = 1; // Adjuntar done

  return (
    <div className="progress-stages">
      {STAGES.map((label, i) => {
        let cls = "stage";
        if (i < avanzado) cls += " done";
        else if (i === avanzado && (enviando || cerrado)) cls += " current";
        return (
          <div key={label} className={cls}>
            {label}
          </div>
        );
      })}
    </div>
  );
}
